import clsx from 'clsx'
import { campaignHooks } from 'features/campaigns'
import {
	type FormEvent,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState
} from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Select, { type MultiValue, type SingleValue } from 'react-select'
import { useAutomationEnded } from 'shared/connector/hooks.ts'
import { Button, LoadingButton } from 'shared/ui/index.ts'

import type { ApiHubSpot } from '../../../api/hubspot'

import { hubSpotApi } from '../../../api/hubspot/api.ts'
import { FullLoader } from '../../atoms/FullLoader/FullLoader'
import css from './CampaignsHubspot.module.scss'
import { adaptToServer, adaptToState, isSame, type Values } from './utils'

const INITIAL: Values = {
	lead: [],
	sub: undefined
}

export function CampaignsHubspot() {
	campaignHooks.useValidId()

	const { campaign, id, refetch } = campaignHooks.useCurrent()

	useAutomationEnded(() => {
		refetch()
	})

	const sourceId = useId()
	const subSourceId = useId()
	const navigate = useNavigate()

	const optionsQuery = hubSpotApi.useOptionsQuery('lead_source')
	const subOptionsQuery = hubSpotApi.useOptionsQuery('contact_sub_source')
	const settingsQuery = hubSpotApi.useHubSpotSettingsQuery(id)
	const [update, updateOptions] = hubSpotApi.useUpdateSettingsMutation()

	const sourceOptions = Array.isArray(optionsQuery.data)
		? optionsQuery.data
		: INITIAL.lead
	const subSourceOptions = Array.isArray(subOptionsQuery.data)
		? subOptionsQuery.data
		: INITIAL.lead
	const [lead, setLead] = useState(INITIAL.lead)
	const campaignName = campaign?.name ?? ''
	const [sub, setSub] = useState(INITIAL.sub)

	const initial = useRef(INITIAL)

	const isInitialValues = useMemo(
		() => isSame({ lead, sub }, initial.current),
		[sub, lead]
	)

	useEffect(() => {
		if (settingsQuery.data && sourceOptions.length && subSourceOptions.length) {
			const fields = settingsQuery.data.static_fields

			initial.current = adaptToState(fields, sourceOptions, subSourceOptions)
			setLead(initial.current.lead)
			setSub(initial.current.sub)
		}
	}, [sourceOptions, subSourceOptions, settingsQuery.data])

	const backLink = `/campaigns/${id}`

	async function sendForm(event: FormEvent<HTMLFormElement>) {
		event.preventDefault()

		const { data } = await update({
			id,
			static_fields: adaptToServer({ lead, sub })
		})

		if (data) {
			navigate(backLink)
		}
	}

	const isLoading =
		settingsQuery.isLoading ||
		optionsQuery.isLoading ||
		subOptionsQuery.isLoading

	return isLoading ? (
		<FullLoader size={40} />
	) : (
		<form className={css.root} onSubmit={sendForm}>
			<div className={clsx('h-2', css.title)}>{campaignName}</div>
			<div className={css.wrapper}>
				<div className={css.dropdown}>
					<label className={clsx('h-5', css.label)} htmlFor={sourceId}>
						Source:
					</label>
					<Select
						id={sourceId}
						isMulti
						onChange={
							setLead as (value: MultiValue<ApiHubSpot.Options>) => void
						}
						options={sourceOptions}
						placeholder='Select...'
						value={lead}
					/>
				</div>

				<div className={css.dropdown}>
					<label className={clsx('h-5', css.label)} htmlFor={subSourceId}>
						Sub Source:
					</label>
					<Select
						id={subSourceId}
						isClearable
						onChange={
							setSub as (value: SingleValue<ApiHubSpot.Options>) => void
						}
						options={subSourceOptions}
						placeholder='Select...'
						value={sub}
					/>
				</div>

				<div className={css.buttons}>
					<Button
						as={Link}
						className={css.button}
						to={backLink}
						variant='secondary'
					>
						Cancel
					</Button>

					<LoadingButton
						className={css.button}
						disabled={isInitialValues}
						isLoading={updateOptions.isLoading}
						type='submit'
					>
						Save
					</LoadingButton>
				</div>
			</div>
		</form>
	)
}
